import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { AuthError } from '@yuan-math/auth';
import { levelForXp } from '@yuan-math/learning';
import { Badge, Button, Card, SectionHeading } from '@yuan-math/ui';
import { useAuth } from '../state/AuthContext.tsx';
import { useLearner } from '../state/LearnerContext.tsx';
import { exportProfile } from '../state/storage.ts';
import { joinClass } from '../state/classroom.ts';
import { openBillingPortal } from '../state/billing.ts';
import { FEATURES } from '@yuan-math/config';
import { AccountsUnavailable } from './RegisterPage.tsx';

/**
 * The user profile and account settings.
 *
 * Two things live here that the design brief treats as promises rather than
 * features (§29): every piece of data the platform holds is listed plainly, and
 * both export and deletion are one click, with no retention period.
 */
export function AccountPage(): JSX.Element {
  const { account, configured, ready, deleteAccount, awaitingConsent, requestGuardianConsent } = useAuth();
  // The learner-level sign-out, which ends the session *and* clears this
  // device. Using the auth-level one here would leave the previous learner's
  // progress sitting on a shared computer.
  const { profile, subscription, entitlements, allowance, signOut } = useLearner();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [consentMessage, setConsentMessage] = useState<string | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [joinMessage, setJoinMessage] = useState<string | null>(null);

  if (!configured) return <AccountsUnavailable />;

  // Same reason as `RegisterPage`: a session still being restored is not an
  // absent one, and "You are not signed in" is a distressing thing to read on
  // your own account page.
  if (!ready) return <div className="route-loading" aria-busy="true" />;

  if (!account) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">You are not signed in</h1>
        <p className="page__lead">Your progress is saved in this browser only.</p>
        <div className="button-row">
          <Link to="/signin" className="btn btn--primary">Sign in</Link>
          <Link to="/register" className="btn btn--secondary">Create an account</Link>
        </div>
      </div>
    );
  }

  const level = profile ? levelForXp(profile.xp) : null;

  const download = (): void => {
    if (!profile) return;
    const blob = new Blob([exportProfile(profile)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `yuan-math-${profile.displayName.toLowerCase().replace(/\s+/g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const remove = async (): Promise<void> => {
    if (!window.confirm('Delete this account and everything in it? This cannot be undone.')) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAccount();
    } catch (cause) {
      setError(cause instanceof AuthError ? cause.message : 'Could not delete the account just now.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page page--wide">
      <header className="page-header">
        <h1 className="page__title">{profile?.displayName ?? 'Your account'}</h1>
        <p className="page__lead">Your account, your data, and what we do and do not hold.</p>
      </header>

      {awaitingConsent ? (
        <Card className="consent-notice">
          <h2 className="consent-notice__title">Waiting for a parent or guardian</h2>
          <p>
            We have emailed <strong>{account.guardianEmail}</strong> to confirm this account. Learning works in the
            meantime; the confirmation is what lets progress sync between devices.
          </p>
          <div className="button-row">
            <Button
              variant="secondary"
              onClick={() => {
                // The provider already wrote a sentence for the specific
                // failure. Re-deciding it here is how this button came to
                // report a working mail server as switched off.
                void requestGuardianConsent().then((result) => setConsentMessage(result.message));
              }}
            >
              Send the email again
            </Button>
          </div>
          {consentMessage ? <p className="field__hint" role="status">{consentMessage}</p> : null}
        </Card>
      ) : null}

      <section className="dashboard-section">
        <SectionHeading title="Account" />
        <Card className="account-card">
          <dl className="account-facts">
            <div>
              <dt>Email</dt>
              <dd>{account.email}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>
                {account.status === 'active' ? <Badge tone="correct">Active</Badge> : <Badge tone="warn">Awaiting activation</Badge>}
              </dd>
            </div>
            <div>
              <dt>Account type</dt>
              <dd>{account.kind === 'child' ? 'Child account' : account.kind === 'guardian' ? 'Guardian' : 'Standard'}</dd>
            </div>
            <div>
              <dt>Member since</dt>
              <dd>{new Date(account.createdAt).toLocaleDateString()}</dd>
            </div>
            {level ? (
              <div>
                <dt>Level</dt>
                <dd>
                  {level.level} · {level.title}
                </dd>
              </div>
            ) : null}
          </dl>
        </Card>
      </section>

      <section className="dashboard-section">
        <SectionHeading title="Plan" description="What your account includes today." />
        <Card className="plan-card">
          <div className="plan-card__head">
            <div>
              <p className="plan-card__tier">
                {entitlements.tier === 'free' ? 'Free' : entitlements.tier === 'premium' ? 'Premium' : entitlements.tier === 'family' ? 'Family' : 'School'}
              </p>
              {subscription?.cancelAtPeriodEnd && subscription.currentPeriodEnd ? (
                <p className="plan-card__note">
                  Cancelled — your access continues until{' '}
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}.
                </p>
              ) : subscription?.status === 'past_due' ? (
                <p className="plan-card__note">
                  A payment did not go through. Nothing has been interrupted; updating your card will settle it.
                </p>
              ) : subscription?.currentPeriodEnd ? (
                <p className="plan-card__note">
                  Renews {new Date(subscription.currentPeriodEnd).toLocaleDateString()}.
                </p>
              ) : (
                <p className="plan-card__note">
                  {allowance.limit === undefined
                    ? 'Unlimited practice.'
                    : `${allowance.limit} practice questions a day. Every lesson and the whole Math Map are always included.`}
                </p>
              )}
            </div>
            {subscription ? (
              <Button
                variant="secondary"
                onClick={() => {
                  void openBillingPortal().then((result) => {
                    if ('url' in result) window.location.href = result.url;
                    else setError(result.error);
                  });
                }}
              >
                Manage billing
              </Button>
            ) : FEATURES.payments ? (
              <Link to="/pricing" className="btn btn--secondary btn--sm">
                See the plans
              </Link>
            ) : null}
            {/* `Manage billing` is deliberately *not* behind the flag. Turning
                payments off closes the way in, and it must never close the way
                out: anyone already holding a subscription keeps the portal that
                cancels it. Only the upsell above is withheld. */}
          </div>

          {allowance.limit !== undefined ? (
            <p className="plan-card__usage">
              {allowance.usedToday} of {allowance.limit} practice questions used today.
            </p>
          ) : null}
        </Card>
      </section>

      <section className="dashboard-section">
        <SectionHeading
          title="What we hold about you"
          description="The complete list. Not a summary of it."
        />
        <Card className="privacy-card">
          <ul className="prose-list">
            <li>The email address above, used to sign in and to send activation.</li>
            <li>A display name you chose. A nickname is fine and is what we suggest.</li>
            <li>An age band — not a birthday. It sets the size and density of the interface.</li>
            <li>Your own work: lessons opened, questions answered, and how each went.</li>
            {account.guardianEmail ? <li>A guardian’s email address, used only for consent.</li> : null}
            {subscription ? (
              <li>
                A reference to your Stripe customer record. <strong>No card details</strong> — those are held by
                Stripe and never reach us.
              </li>
            ) : null}
          </ul>
          <p className="page__note">
            No date of birth, no school, no location, and no advertising identifiers. Visits are counted with
            Cloudflare Web Analytics, which uses no cookies and cannot follow anyone between sites. See{' '}
            <Link to="/privacy">the privacy notice</Link>.
          </p>
        </Card>
      </section>

      {FEATURES.teacherDashboard ? (
        <section className="dashboard-section">
          <SectionHeading
            title="Join a class"
            description="If a teacher has given you a code, entering it lets them see your mastery — not your individual answers."
          />
          <Card>
            <form
              className="answer-form__row"
              onSubmit={(event) => {
                event.preventDefault();
                setJoinMessage(null);
                void joinClass(joinCode).then((joined) =>
                  setJoinMessage(
                    joined ? `You have joined ${joined.name}.` : 'No open class with that code. Check it with your teacher.',
                  ),
                );
              }}
            >
              <input
                className="field__input join-input"
                value={joinCode}
                onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={8}
                aria-label="Class join code"
              />
              <Button type="submit" variant="secondary" disabled={joinCode.trim().length < 4}>
                Join
              </Button>
            </form>
            {joinMessage ? <p className="field__hint" role="status">{joinMessage}</p> : null}
            <p className="page__note">
              You can leave a class at any time, and your teacher can never add you without you entering the code.
            </p>
          </Card>
        </section>
      ) : null}

      <section className="dashboard-section">
        <SectionHeading title="Your data" description="Yours to take or to remove, immediately." />
        <div className="button-row">
          <Button variant="secondary" onClick={download} disabled={!profile}>
            Download everything
          </Button>
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
          <Button variant="danger" onClick={() => void remove()} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete account'}
          </Button>
        </div>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <p className="page__note">
          Deleting removes the account and every piece of learning data with it, at once. There is no retention
          period and no soft delete.
        </p>
      </section>
    </div>
  );
}
